
// Service Worker Registration Utility
import React from 'react';

const isServiceWorkerSupported = 'serviceWorker' in navigator;

/**
 * Registers the service worker for production environments
 */
export function register() {
  if (isServiceWorkerSupported && import.meta.env.PROD) {
    window.addEventListener('load', () => {
      const swUrl = '/sw.js';

      registerValidSW(swUrl);

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('New service worker controller, page will reload');
        window.location.reload();
      });
    });
  }
}

/**
 * Registers the service worker and handles updates
 * @param {string} swUrl - The service worker URL
 */
function registerValidSW(swUrl: string) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      console.log('ServiceWorker registration successful with scope: ', registration.scope);

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available, show refresh notification
              console.log('New content is available; please refresh');
              showUpdateNotification();
            } else {
              // Content is cached for offline use
              console.log('Content is cached for offline use');
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error('Error during service worker registration:', error);
    });
}

/**
 * Shows a notification that an update is available
 */
function showUpdateNotification() {
  // Check if the browser supports notifications
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('Cats About Town Tours', {
      body: 'New version available. Click to update!',
      icon: '/icons/icon-192x192.png',
    });
  } else {
    // In-app toast notification
    import('@/hooks/use-toast').then(({ toast }) => {
      import('@/components/ui/toast').then(({ ToastAction }) => {
        toast({
          title: "Update available",
          description: "A new version of the app is available. Refresh to update.",
          action: <ToastAction onClick={() => window.location.reload()} altText="Reload">
            Reload
          </ToastAction>,
        });
      });
    });
  }
}

/**
 * Unregisters the service worker (useful for development)
 */
export function unregister() {
  if (isServiceWorkerSupported) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

/**
 * Requests permission for push notifications
 * @returns {Promise<boolean>} Whether permission was granted
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}
